/**
 * Every default of the Modbus heat pump appliance ("Air-to-water heat pump NEW",
 * appliance type HEAT_PUMP, port 0x23) in one place.
 *
 * WIP. The firmware's heat pump channel on optimizer_control (spp_optimizer_control.h,
 * "HEAT-PUMP CHANNELS"): power levels 0..7 are electrical input targets of
 * level/7 x appliance_power; the duty fields describe the house's heat demand
 * line (electrical duty at two outdoor temperatures); the comfort setpoint is
 * (min_temp + max_temp) / 2, and its offset from 22 degC shifts the indoor
 * temperature (open loop: the demand line's balance point; with an indoor
 * sensor: the real target).
 */
import { PortConfigHeatPump, PortNumber } from '../../types/ports'

/**
 * The PORT_HEATPUMP conf (spp_heatpump_conf_t) written for a new appliance.
 * Zero in a numeric field means "firmware default" (see spp_heatpump.h).
 */
export const HEAT_PUMP_CONF_DEFAULTS: PortConfigHeatPump = {
  enabled: false,
  driver: 'HEATPUMP_DRIVER_MIDEA_MTHERMAL',
  // Modbus RTU on RS485 port 4/0; 4/1 is HEATPUMP_CONN_RTU_RS485_1.
  conn_type: 'HEATPUMP_CONN_RTU_RS485_0',
  // Midea: "HMI address for BMS" in the FOR SERVICEMAN menu.
  slave_id: 1,
  // 9600 8N1 (driver default).
  baud_sel: 0,
  parity: 0,
  force_ip_last_octet: 0,
  failsafe_release_minutes: 0,
  cmd_valid_minutes: 0,
  // The power tracker needs the flow setpoint; target 0 may switch the zone off.
  // DHW stays with the machine in V1; silent-mode capping is never allowed (COP penalty).
  allow_setpoint: true,
  allow_zone_off: true,
  allow_dhw_control: false,
  flow_setpoint_min: 0,
  flow_setpoint_max: 45,
  dhw_setpoint_normal: 0,
  dhw_setpoint_boost: 0,
  // 0 = the driver's learned estimate; only scales manual CMD_SET demand tests.
  rated_elec_power: 0,
  rated_aux_power: 0,
  poll_interval_s: 0,
  zone_mask: 0,
}

/** Ceiling for the flow setpoints the driver writes, by heating system. */
export const HEAT_PUMP_FLOW_MAX_PRESETS: Record<'UNDERFLOOR' | 'RADIATORS', number> = {
  UNDERFLOOR: 45,
  RADIATORS: 55,
}

/** Accepted range of the maximum flow temperature field, degC. */
export const HEAT_PUMP_FLOW_MAX_RANGE: [number, number] = [25, 65]

/**
 * Optimizer control instances the appliance may take, in order of
 * preference. 211/0..211/5 belong to the relays and the EV charger.
 */
export const HEAT_PUMP_OPTIMIZER_INSTANCES: PortNumber[] = ['211/6' as PortNumber, '211/7' as PortNumber]

/**
 * Per-house calibration of the heat demand model: not a user setting (installer
 * section only). Electrical duty in % of appliance_power at two outdoor
 * temperatures; appliance_power = the machine's maximum space-heating electrical
 * input in cold weather, backup heaters excluded (= power level 7).
 */
export interface HeatPumpCalibration {
  appliance_power: number
  min_duty: number
  max_duty: number
  min_duty_at: number
  max_duty_at: number
}

export const HEAT_PUMP_CALIBRATION_DEFAULTS: HeatPumpCalibration = {
  appliance_power: 3500,
  // DHW is the machine's own job in V1: a nonzero min_duty would push extra heat into the house.
  min_duty: 0,
  max_duty: 95,
  // Heating limit and design outdoor temperatures, degC.
  min_duty_at: 16,
  max_duty_at: -15,
}

/** The stored calibration completed with the defaults. */
export function heatPumpCalibration(stored: Partial<HeatPumpCalibration> | undefined): HeatPumpCalibration {
  const ret = { ...HEAT_PUMP_CALIBRATION_DEFAULTS }
  for (const key of Object.keys(ret) as (keyof HeatPumpCalibration)[]) {
    const value = stored?.[key]
    if (typeof value === 'number' && Number.isFinite(value)) ret[key] = value
  }
  return ret
}

/**
 * Comfort band around the 22 degC reference; the slider shifts it by up to
 * HEAT_PUMP_COMFORT.maxShift K.
 */
export const HEAT_PUMP_COMFORT = {
  minTemp: 21,
  maxTemp: 23,
  maxShift: 3,
  // abs_min/abs_max = setpoint -/+ 5 (as in the firmware simulation); only used with a sensor.
  absMargin: 4,
}

/**
 * "More saving / More heat" (-100..100) -> [min_temp, max_temp], as agreed with
 * the firmware (spp_optimizer_control.h, HEAT-PUMP CHANNELS): sp = 22 + offset,
 * min_temp = floor(sp) - 1, max_temp = 2 sp - min_temp, so that the firmware's
 * setpoint (min + max) / 2 is sp. Slider 0 = 21 / 23.
 */
export function heatPumpComfortBand(savingOrHeat: number): [number, number] {
  const sp = (HEAT_PUMP_COMFORT.minTemp + HEAT_PUMP_COMFORT.maxTemp) / 2 + heatPumpComfortOffset(savingOrHeat)
  const minTemp = Math.floor(sp) - 1
  return [minTemp, 2 * sp - minTemp]
}

/**
 * The indoor temperature offset the slider asks for: 3 K x slider / 100,
 * rounded to 0.5 K (halves away from zero, the same for both directions).
 */
export function heatPumpComfortOffset(savingOrHeat: number): number {
  const s = Math.max(-100, Math.min(100, savingOrHeat || 0))
  const halves = 2 * HEAT_PUMP_COMFORT.maxShift * s / 100
  return Math.sign(halves) * Math.round(Math.abs(halves)) / 2
}

/** optimizer_control fields the heat pump appliance always writes. */
export const HEAT_PUMP_OPTIMIZER_DEFAULTS = {
  // All zeroes = the firmware's built-in air-to-water COP curve.
  cop_curve: [0, 0, 0, 0] as [number, number, number, number],
  good_sink: false,
  rapid_switching_ok: false,
  use_clock_based_duty: false,
  // Not in fuse protection by default: the machine follows target changes in minutes.
  fuse_priority: 0,
  pv_priority: 1,
  time_constant: 8,
}
